/**
 * Modal entrypoint — the thin esbuild bundle that boots the modal registry and
 * the one delegated upgrade listener for foreign (eModal/quickView) shells.
 *
 * Per the build convention, `src-components/**` is library-only (never an
 * esbuild entrypoint); this file is the entrypoint that owns the DOM-ready hook
 * and delegates all logic to the shared library. Bundled to `dist/js/modal.js`;
 * the modal partial emits its `<script defer>` once via `{datte_once}`.
 *
 * Two init paths, ONE entrypoint (JG1):
 *   1. `initModalRegistry()` wires every `.datte-modal` already in the DOM
 *      (theme modals + the SCC/parent-snippet overrides that render our shell).
 *   2. One delegated `shown.bs.modal` listener upgrades genuinely template-less
 *      foreign shells (eModal .alert/.confirm/.ajax, jtl.article.js quickView,
 *      and our #5 incidentally — §5.1) the moment BS4 finishes opening them.
 *      BS4 broadcasts `shown.bs.modal` for EVERY shell, so this one listener
 *      covers the whole C-family with no per-call-site or core-JS change (JG6).
 *
 * Delegated, not a MutationObserver: BS4 already emits the lifecycle event after
 * the shell is in the DOM and initialised — cheaper, ordering-correct, and
 * engine-consistent (spec §5 NOTE).
 *
 * @see docs/architecture/modals/target-architecture.md §1a.3 (registry entrypoint)
 * @see docs/architecture/modals/jtl-integration-architecture.md §5 (runtime adapter)
 */

import { initModalRegistry } from '../../src-components/modal/modal-registry';
import {
  shouldUpgrade,
  upgradeForeignModal,
  type UpgradeOptions,
} from '../../src-components/modal/modal-upgrade';
import { initGalleryLightboxAdapter } from '../../src-components/modal/gallery-lightbox-adapter';
import { installArticleRerenderGuard } from '../../src-components/history/article-rerender-guard';

/** Minimal jQuery surface this entrypoint needs for the delegated listener. */
interface JqDelegate {
  (selector: string | Document): {
    on(
      event: string,
      selector: string,
      handler: (this: HTMLElement, e: { target: EventTarget | null }) => void
    ): void;
  };
}

function getJq(): JqDelegate | undefined {
  const win = window as Window & { jQuery?: JqDelegate; $?: JqDelegate };
  return win.jQuery ?? win.$;
}

/**
 * Per-family upgrade options for a foreign shell. eModal alerts/confirms are
 * transient → historyBack OFF (a back-press must not be eaten by a transient
 * confirm, spec §N item 6). All foreign shells get focus-trap + the mobile sheet.
 * The variant `datte-emodal` is a CSS hook for any eModal-specific tuning.
 */
const FOREIGN_UPGRADE_OPTS: UpgradeOptions = {
  variant: 'datte-emodal',
  historyBack: false,
  focusTrap: true,
  fullscreenMobile: true,
};

/**
 * Wire the single delegated `shown.bs.modal` listener that upgrades foreign
 * shells. No-op when jQuery is absent (no BS4 → no foreign shells to upgrade).
 */
function initForeignUpgradeListener(): void {
  const jq = getJq();
  if (!jq) return;

  jq(document).on('shown.bs.modal', '.modal', function onShown() {
    if (shouldUpgrade(this)) {
      upgradeForeignModal(this, FOREIGN_UPGRADE_OPTS);
    }
  });
}

/**
 * Upgrade the parent's `#login-popup` (D1, on every page, high-traffic) eagerly
 * on DOM-ready with login-specific options, BEFORE the generic `shown.bs.modal`
 * listener can stamp it with the default eModal opts. It is in the DOM from page
 * load (header), so no need to wait for open. Tagging it here makes the generic
 * listener a no-op for it (already `.datte-modal`, JG3). The parent renders it as
 * a reachable `{block}` (Snackys/layout/header_shopnav_user.tpl) but overriding
 * that block would fork the parent's online/offline auth markup; the adapter is
 * the additive, JG7-safe path (spec §4.4 branch 2). historyBack on so Android
 * back closes it; the corner/header close-style is derived from its `.modal-header`.
 */
function upgradeLoginPopup(): void {
  const el = document.getElementById('login-popup');
  if (!el) return;
  upgradeForeignModal(el, {
    variant: 'datte-login-modal',
    historyBack: true,
    focusTrap: true,
    fullscreenMobile: true,
  });
}

function boot(): void {
  // FIRST: the parent's article re-render must be guarded before any overlay can
  // push a history entry, or the first close of the session re-renders the product
  // area and discards the customer's configuration. Safe and silent on pages with
  // no `$.evo.article` (see article-rerender-guard.ts).
  installArticleRerenderGuard();
  initModalRegistry();
  initForeignUpgradeListener();
  upgradeLoginPopup();
  // Light-touch unification of the parent product-image lightbox (ADR-0008 D4):
  // it is the parent's `body.lightbox-shown` overlay, NOT a Bootstrap modal, so it
  // is invisible to the registry/upgrade paths above. This adapter layers ONLY the
  // shared close UX (Escape / browser-Back / backdrop) onto the parent's existing
  // state and delegates the actual close to `#close-lightbox` — no gallery fork.
  // No-op on pages without a gallery (guarded internally).
  initGalleryLightboxAdapter();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
